import React, { useEffect, useMemo, useState } from "react";
import { Loader2, LogOut, Copy, Check, ChevronDown, ChevronRight, Download } from "lucide-react";
import Wordmark from "../components/Wordmark";
import {
  adminHealth,
  adminList,
  adminApproveDataRoom,
  adminRejectDataRoom,
  adminLogs,
} from "../lib/api";

/**
 * `/admin` — spartan operations cockpit (Phase 7).
 *
 * Not linked from any user-facing route. Direct-URL page, bearer-token gated.
 * The token lives in `localStorage.gg_admin_token`. On mount we validate it
 * against `GET /api/admin/health`; if invalid, we show a token-entry screen.
 */

const STORAGE_KEY = "gg_admin_token";
const KINDS = [
  { key: "rfp", label: "Sovereign Audit RFPs", api: "rfp", refField: "ref_id", primary: (r) => r?.contact?.org || r?.contact?.name || "—" },
  { key: "data-room/requests", label: "Data-Room access requests", api: "data-room/requests", refField: "ref_id", primary: (r) => r.org || r.name || "—" },
  { key: "data-room/partnerships", label: "Partnership RFPs", api: "data-room/partnerships", refField: "ref_id", primary: (r) => r.session_org || "—" },
  { key: "deals", label: "Partner deal registrations", api: "deals", refField: "ref_id", primary: (r) => `${r.partner_org} → ${r.client_org}` },
  { key: "reservations", label: "Sovereign Block reservations", api: "reservations", refField: "ref_id", primary: (r) => r.name || r.email || "—" },
  { key: "visits", label: "Visit bookings", api: "visits", refField: "ref_id", primary: (r) => `${r.visit_type} · ${r.name}` },
];

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [validated, setValidated] = useState(false);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState("");

  // Validate stored token on mount (or when the user pastes one).
  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setValidated(false);
      return;
    }
    setValidating(true);
    adminHealth(token)
      .then(() => { if (!cancelled) { setValidated(true); setError(""); } })
      .catch((ex) => {
        if (cancelled) return;
        setValidated(false);
        if (ex?.response?.status === 401) {
          setError("That token isn't recognised. Check with the deployer.");
        } else {
          setError("Unable to reach the admin API. Try again in a moment.");
        }
      })
      .finally(() => { if (!cancelled) setValidating(false); });
    return () => { cancelled = true; };
  }, [token]);

  const onLogin = (raw) => {
    localStorage.setItem(STORAGE_KEY, raw);
    setToken(raw);
  };
  const onLogout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setToken("");
    setValidated(false);
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }} data-testid="admin-page">
      <header
        style={{
          padding: "22px clamp(20px, 5vw, 60px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid var(--line)",
          background: "rgba(10, 22, 40, 0.72)",
          backdropFilter: "blur(14px)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <Wordmark />
          <span
            style={{
              padding: "3px 10px",
              borderRadius: 999,
              background: "rgba(244,169,31,0.12)",
              border: "1px solid rgba(244,169,31,0.35)",
              color: "var(--gold-soft)",
              fontSize: 10.5,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
            }}
            data-testid="admin-badge"
          >
            Operations · Admin
          </span>
        </div>
        {validated && (
          <button
            type="button"
            onClick={onLogout}
            data-testid="admin-logout"
            style={{
              background: "transparent",
              border: "1px solid var(--line)",
              borderRadius: 999,
              padding: "8px 14px",
              color: "var(--text-dim)",
              fontSize: 13,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <LogOut size={13} /> Log out
          </button>
        )}
      </header>

      <main style={{ flex: 1, padding: "clamp(32px, 5vw, 60px) clamp(20px, 5vw, 60px)" }}>
        {!validated ? (
          <TokenEntry onLogin={onLogin} validating={validating} error={error} />
        ) : (
          <Cockpit token={token} />
        )}
      </main>
    </div>
  );
}

// ============ Token entry ============
function TokenEntry({ onLogin, validating, error }) {
  const [raw, setRaw] = useState("");
  return (
    <section style={{ maxWidth: 520, margin: "0 auto", paddingTop: "clamp(40px, 8vw, 100px)" }} data-testid="admin-token-entry">
      <div className="gg-eyebrow" style={{ marginBottom: 10 }}>Restricted area</div>
      <h1 className="gg-h1" style={{ marginBottom: 12 }}>Enter the admin token.</h1>
      <p style={{ color: "var(--text-dim)", lineHeight: 1.7, marginBottom: 26, fontSize: 15 }}>
        Two or three people at Green Gold have a copy of this token. If you
        don't have it, close this tab.
      </p>
      <form
        onSubmit={(e) => { e.preventDefault(); if (raw.trim()) onLogin(raw.trim()); }}
        style={{ display: "flex", flexDirection: "column", gap: 12 }}
      >
        <input
          type="password"
          autoFocus
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          data-testid="admin-token-input"
          placeholder="Paste your token here"
          style={{
            width: "100%",
            padding: "14px 16px",
            borderRadius: 12,
            background: "rgba(255,255,255,0.04)",
            border: "1px solid var(--line-strong)",
            color: "var(--paper)",
            fontSize: 15,
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            outline: "none",
          }}
        />
        <button
          type="submit"
          disabled={!raw.trim() || validating}
          data-testid="admin-token-submit"
          style={{
            padding: "12px 18px",
            borderRadius: 12,
            border: "none",
            background: "linear-gradient(90deg, var(--gold-deep), var(--gold))",
            color: "var(--night)",
            fontWeight: 600,
            fontSize: 14.5,
            cursor: raw.trim() ? "pointer" : "not-allowed",
            opacity: raw.trim() ? 1 : 0.4,
            display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
          }}
        >
          {validating ? <><Loader2 size={14} className="animate-spin" /> Validating…</> : "Unlock"}
        </button>
        {error && (
          <p style={{ color: "#ff8a8a", fontSize: 13.5, marginTop: 4 }} data-testid="admin-token-error">
            {error}
          </p>
        )}
      </form>
    </section>
  );
}

// ============ Cockpit ============
function Cockpit({ token }) {
  const [data, setData] = useState({}); // {kind: {total, items}}
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(KINDS[0].key);
  const [expanded, setExpanded] = useState(null); // ref_id

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all(
      KINDS.map((k) => adminList(token, k.api).then((r) => [k.key, r]).catch(() => [k.key, { total: 0, items: [] }])),
    ).then((pairs) => {
      if (cancelled) return;
      setData(Object.fromEntries(pairs));
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [token]);

  const funnel = useMemo(() => buildFunnel(data), [data]);

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", paddingTop: 80 }} data-testid="admin-cockpit-loading">
        <Loader2 className="animate-spin" size={28} color="var(--gold-soft)" />
      </div>
    );
  }

  const activeKind = KINDS.find((k) => k.key === tab);
  const activeData = data[tab] || { items: [], total: 0 };

  return (
    <section style={{ maxWidth: 1200, margin: "0 auto" }} data-testid="admin-cockpit">
      <FunnelBand funnel={funnel} />

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, margin: "36px 0 20px" }} data-testid="admin-tabs">
        {KINDS.map((k) => (
          <button
            key={k.key}
            type="button"
            onClick={() => { setTab(k.key); setExpanded(null); }}
            data-testid={`admin-tab-${k.key.replace(/[/]/g, "-")}`}
            style={{
              padding: "8px 14px",
              borderRadius: 999,
              border: `1px solid ${tab === k.key ? "rgba(244,169,31,0.5)" : "var(--line)"}`,
              background: tab === k.key ? "rgba(244,169,31,0.1)" : "transparent",
              color: tab === k.key ? "var(--gold-soft)" : "var(--text-dim)",
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            {k.label}
            <span style={{ marginLeft: 8, color: "var(--text-mute)", fontSize: 11.5 }}>
              {(data[k.key]?.total ?? 0)}
            </span>
          </button>
        ))}
      </div>

      {tab === "data-room/requests" ? (
        <DataRoomTable token={token} items={activeData.items} onRefresh={async () => {
          const r = await adminList(token, "data-room/requests");
          setData((d) => ({ ...d, [tab]: r }));
        }} />
      ) : (
        <GenericTable kind={activeKind} items={activeData.items} expanded={expanded} setExpanded={setExpanded} />
      )}

      <LogsSection token={token} />
    </section>
  );
}

// ============ Funnel band ============
function buildFunnel(data) {
  const now = Date.now();
  const cutoff = now - 7 * 24 * 60 * 60 * 1000;
  const byDay = Array.from({ length: 7 }, () => 0);
  let total = 0;
  const perKind = {};

  KINDS.forEach((k) => {
    const items = data[k.key]?.items || [];
    let count = 0;
    items.forEach((it) => {
      const tsRaw = it.received_at || it.registered_at || it.created_at;
      if (!tsRaw) return;
      const ts = new Date(tsRaw).getTime();
      if (ts >= cutoff && ts <= now) {
        const dayIdx = Math.floor((now - ts) / (24 * 60 * 60 * 1000));
        const bucketIdx = 6 - Math.min(6, Math.max(0, dayIdx));
        byDay[bucketIdx] += 1;
        count += 1;
        total += 1;
      }
    });
    perKind[k.key] = count;
  });
  return { byDay, total, perKind };
}

function FunnelBand({ funnel }) {
  const max = Math.max(1, ...funnel.byDay);
  return (
    <div
      className="gg-glass"
      style={{ padding: 22, display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.4fr)", gap: 32 }}
      data-testid="admin-funnel"
    >
      <div>
        <div className="gg-eyebrow" style={{ marginBottom: 8 }}>Last 7 days</div>
        <div style={{ fontFamily: "Fraunces, Georgia, serif", fontSize: 38, color: "var(--paper)", lineHeight: 1 }} data-testid="admin-funnel-total">
          {funnel.total}
        </div>
        <div style={{ color: "var(--text-dim)", fontSize: 13, marginTop: 4 }}>submissions across all types</div>
        <div style={{ marginTop: 18, display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 6, fontSize: 12.5 }}>
          {KINDS.map((k) => (
            <div key={k.key} style={{ color: "var(--text-dim)" }}>
              <span style={{ color: "var(--gold-soft)", fontFamily: "Fraunces, Georgia, serif", fontSize: 14 }}>
                {funnel.perKind[k.key] || 0}
              </span>{" "}
              {k.label.replace(/registrations| access requests| bookings| RFPs/, "")}
            </div>
          ))}
        </div>
      </div>

      {/* Sparkline (SVG, no library) */}
      <div style={{ display: "flex", flexDirection: "column" }}>
        <div className="gg-eyebrow" style={{ marginBottom: 10 }}>Daily activity</div>
        <svg width="100%" height="110" viewBox="0 0 350 110" preserveAspectRatio="none" data-testid="admin-sparkline">
          {funnel.byDay.map((v, i) => {
            const barW = 350 / 7 - 8;
            const x = i * (350 / 7) + 4;
            const h = (v / max) * 90;
            const y = 100 - h;
            return (
              <g key={i}>
                <rect
                  x={x}
                  y={y}
                  width={barW}
                  height={Math.max(2, h)}
                  fill="url(#gg-spark-grad)"
                  rx="3"
                />
                <text x={x + barW / 2} y={108} textAnchor="middle" fontSize="9" fill="var(--text-mute)">
                  D{i - 6}
                </text>
              </g>
            );
          })}
          <defs>
            <linearGradient id="gg-spark-grad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#ffd479" stopOpacity="0.9" />
              <stop offset="100%" stopColor="#f4a91f" stopOpacity="0.4" />
            </linearGradient>
          </defs>
        </svg>
      </div>
    </div>
  );
}

// ============ Generic table ============
function GenericTable({ kind, items, expanded, setExpanded }) {
  if (!items.length) {
    return (
      <div data-testid={`admin-table-${kind.key.replace(/[/]/g, "-")}-empty`} style={{ color: "var(--text-dim)", padding: 40, textAlign: "center" }}>
        No {kind.label.toLowerCase()} yet.
      </div>
    );
  }
  return (
    <div className="gg-glass" style={{ padding: 0, overflow: "hidden" }} data-testid={`admin-table-${kind.key.replace(/[/]/g, "-")}`}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead style={{ background: "rgba(0,0,0,0.35)" }}>
          <tr style={{ color: "var(--text-mute)", textAlign: "left", fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase" }}>
            <th style={{ padding: "12px 14px", width: 32 }} />
            <th style={{ padding: "12px 14px" }}>Ref</th>
            <th style={{ padding: "12px 14px" }}>Party</th>
            <th style={{ padding: "12px 14px" }}>Received</th>
            <th style={{ padding: "12px 14px" }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {items.map((r) => {
            const isOpen = expanded === r[kind.refField];
            return (
              <React.Fragment key={r[kind.refField]}>
                <tr
                  style={{ borderTop: "1px solid var(--line)", cursor: "pointer" }}
                  onClick={() => setExpanded(isOpen ? null : r[kind.refField])}
                  data-testid={`admin-row-${r[kind.refField]}`}
                >
                  <td style={{ padding: "10px 14px", color: "var(--text-mute)" }}>
                    {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </td>
                  <td style={{ padding: "10px 14px", fontFamily: "ui-monospace, monospace", color: "var(--gold-soft)" }}>
                    {r[kind.refField]}
                  </td>
                  <td style={{ padding: "10px 14px", color: "var(--paper)" }}>{kind.primary(r)}</td>
                  <td style={{ padding: "10px 14px", color: "var(--text-dim)" }}>
                    {formatTs(r.received_at || r.registered_at || r.created_at)}
                  </td>
                  <td style={{ padding: "10px 14px", color: "var(--text-dim)" }}>{r.status || "—"}</td>
                </tr>
                {isOpen && (
                  <tr style={{ borderTop: "1px solid var(--line)", background: "rgba(0,0,0,0.25)" }}>
                    <td colSpan={5} style={{ padding: 16 }}>
                      <RecordDetail record={r} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ============ Data-room table (with approve / reject) ============
function DataRoomTable({ token, items, onRefresh }) {
  const [busy, setBusy] = useState(null);
  const [magic, setMagic] = useState({}); // ref_id -> magic_link
  const [error, setError] = useState("");

  const approve = async (refId) => {
    setBusy(refId); setError("");
    try {
      const r = await adminApproveDataRoom(token, refId);
      setMagic((m) => ({ ...m, [refId]: r.magic_link }));
      await onRefresh();
    } catch (ex) {
      setError(ex?.response?.data?.detail || "Approve failed.");
    } finally { setBusy(null); }
  };
  const reject = async (refId) => {
    const reason = window.prompt("Reason (optional):", "") || null;
    setBusy(refId); setError("");
    try {
      await adminRejectDataRoom(token, refId, reason);
      await onRefresh();
    } catch (ex) {
      setError(ex?.response?.data?.detail || "Reject failed.");
    } finally { setBusy(null); }
  };

  if (!items.length) {
    return <div style={{ color: "var(--text-dim)", padding: 40, textAlign: "center" }} data-testid="admin-dr-table-empty">No access requests yet.</div>;
  }

  return (
    <div className="gg-glass" style={{ padding: 0, overflow: "hidden" }} data-testid="admin-dr-table">
      {error && <p style={{ padding: 12, color: "#ff8a8a" }}>{error}</p>}
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead style={{ background: "rgba(0,0,0,0.35)" }}>
          <tr style={{ color: "var(--text-mute)", textAlign: "left", fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase" }}>
            <th style={{ padding: "12px 14px" }}>Ref</th>
            <th style={{ padding: "12px 14px" }}>Org</th>
            <th style={{ padding: "12px 14px" }}>Ticket</th>
            <th style={{ padding: "12px 14px" }}>Status</th>
            <th style={{ padding: "12px 14px", textAlign: "right" }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map((r) => (
            <React.Fragment key={r.ref_id}>
              <tr style={{ borderTop: "1px solid var(--line)" }} data-testid={`admin-dr-row-${r.ref_id}`}>
                <td style={{ padding: "10px 14px", fontFamily: "ui-monospace, monospace", color: "var(--gold-soft)" }}>{r.ref_id}</td>
                <td style={{ padding: "10px 14px", color: "var(--paper)" }}>{r.org}</td>
                <td style={{ padding: "10px 14px", color: "var(--text-dim)" }}>{r.ticket_band}</td>
                <td style={{ padding: "10px 14px", color: r.status === "approved" ? "var(--green-bright)" : r.status === "rejected" ? "#ff8a8a" : "var(--gold-soft)" }}>{r.status}</td>
                <td style={{ padding: "10px 14px", textAlign: "right" }}>
                  {r.status === "pending" ? (
                    <>
                      <button
                        type="button"
                        onClick={() => approve(r.ref_id)}
                        disabled={busy === r.ref_id}
                        data-testid={`admin-dr-approve-${r.ref_id}`}
                        style={dropButton("rgba(31,170,89,0.35)", "var(--green-bright)")}
                      >
                        {busy === r.ref_id ? "…" : "Approve"}
                      </button>
                      <button
                        type="button"
                        onClick={() => reject(r.ref_id)}
                        disabled={busy === r.ref_id}
                        data-testid={`admin-dr-reject-${r.ref_id}`}
                        style={{ ...dropButton("rgba(255,120,120,0.35)", "#ff8a8a"), marginLeft: 8 }}
                      >
                        Reject
                      </button>
                    </>
                  ) : (
                    <span style={{ color: "var(--text-mute)", fontSize: 12 }}>—</span>
                  )}
                </td>
              </tr>
              {magic[r.ref_id] && (
                <tr style={{ borderTop: "1px solid var(--line)", background: "rgba(31,170,89,0.06)" }}>
                  <td colSpan={5} style={{ padding: 14 }}>
                    <MagicLinkPanel link={magic[r.ref_id]} />
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MagicLinkPanel({ link }) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }} data-testid="admin-magic-panel">
      <span className="gg-eyebrow" style={{ color: "var(--green-bright)" }}>Magic link</span>
      <input
        type="text"
        readOnly
        value={link}
        onFocus={(e) => e.target.select()}
        data-testid="admin-magic-link"
        style={{
          flex: 1,
          minWidth: 260,
          padding: "8px 12px",
          background: "rgba(0,0,0,0.35)",
          border: "1px solid var(--line-strong)",
          borderRadius: 8,
          color: "var(--paper)",
          fontFamily: "ui-monospace, monospace",
          fontSize: 12,
        }}
      />
      <button
        type="button"
        onClick={() => { navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
        data-testid="admin-magic-copy"
        style={dropButton("rgba(244,169,31,0.4)", "var(--gold-soft)")}
      >
        {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
      </button>
    </div>
  );
}

function RecordDetail({ record }) {
  // Redact any PAN-like field: keep only last 4.
  const cleaned = { ...record };
  ["pan_or_passport", "pan"].forEach((k) => {
    if (typeof cleaned[k] === "string" && cleaned[k].length > 4) {
      cleaned[k] = `••••••${cleaned[k].slice(-4)}`;
    }
  });
  return (
    <pre
      data-testid="admin-record-detail"
      style={{
        margin: 0,
        maxHeight: 340,
        overflow: "auto",
        fontFamily: "ui-monospace, monospace",
        fontSize: 12,
        color: "var(--text-cloud)",
        background: "rgba(0,0,0,0.35)",
        padding: 12,
        borderRadius: 8,
      }}
    >
      {JSON.stringify(cleaned, null, 2)}
    </pre>
  );
}

// ============ Logs section ============
function LogsSection({ token }) {
  const [open, setOpen] = useState(null);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchLog = async (name) => {
    setLoading(true);
    try {
      const r = await adminLogs(token, name);
      setContent(r);
      setOpen(name);
    } catch {
      setContent({ log: name, lines: [], error: "Failed to fetch log." });
      setOpen(name);
    } finally { setLoading(false); }
  };

  const download = () => {
    if (!content) return;
    const blob = new Blob([JSON.stringify(content, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${content.log}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <section style={{ marginTop: 50 }} data-testid="admin-logs">
      <h2 className="gg-h2" style={{ marginBottom: 14 }}>Notification logs</h2>
      <p style={{ color: "var(--text-dim)", fontSize: 13.5, marginBottom: 14 }}>
        The last 500 lines of each notification stream. Until real transactional email is wired, these are the outbound signal.
      </p>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {["rfp", "partnership", "deals", "visits", "data_room"].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => fetchLog(n)}
            data-testid={`admin-log-fetch-${n}`}
            style={{
              padding: "8px 14px",
              borderRadius: 999,
              border: `1px solid ${open === n ? "rgba(244,169,31,0.5)" : "var(--line)"}`,
              background: open === n ? "rgba(244,169,31,0.1)" : "transparent",
              color: open === n ? "var(--gold-soft)" : "var(--text-dim)",
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            {n.replace("_", " ")}
          </button>
        ))}
      </div>

      {loading && <div style={{ padding: 20 }}><Loader2 size={18} className="animate-spin" /></div>}

      {content && (
        <div className="gg-glass" style={{ padding: 16, marginTop: 16 }} data-testid="admin-log-content">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <span className="gg-eyebrow">{content.log} · {content.count || 0} lines</span>
            <button
              type="button"
              onClick={download}
              data-testid="admin-log-download"
              style={dropButton("rgba(244,169,31,0.4)", "var(--gold-soft)")}
            >
              <Download size={12} /> Download JSON
            </button>
          </div>
          <pre style={{ margin: 0, maxHeight: 420, overflow: "auto", fontFamily: "ui-monospace, monospace", fontSize: 12, color: "var(--text-cloud)" }}>
            {(content.lines || []).map((l, i) => JSON.stringify(l)).join("\n") || "(empty)"}
          </pre>
        </div>
      )}
    </section>
  );
}

// ============ Utility ============
function formatTs(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
  } catch {
    return iso;
  }
}

function dropButton(border, color) {
  return {
    padding: "6px 12px",
    borderRadius: 999,
    border: `1px solid ${border}`,
    background: "transparent",
    color,
    fontSize: 12,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
  };
}
